import React from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, CheckCircle2, MessageSquare, Route, ShieldCheck, Sparkles, Trophy, Upload, X } from 'lucide-react';
import type { UserTier } from '../../types';
import type { RewardEventKind } from '../../services/rewardEvents';
import { computeTierBarPct, isNearTierGoal } from '../../utils/tierProgress';

export interface ContributionProgressToastState {
  kind: RewardEventKind;
  title: string;
  message: string;
  rewardPoints: number;
  currentTier: UserTier;
  nextTier: UserTier | null;
  uniqueSpots: number;
  spotsToNextTier: number;
}

interface ContributionProgressToastProps {
  state: ContributionProgressToastState;
  onClose: () => void;
}

const ContributionProgressToast: React.FC<ContributionProgressToastProps> = ({ state, onClose }) => {
  const { t } = useTranslation();
  // Endowed progress: kumulativ poengsum (inkl. verifiserte bidrag) mot neste tier,
  // aldri visuelt tom. Nær-mål gir ekstra emphasis. Se utils/tierProgress.ts.
  const progressPct = computeTierBarPct(state.spotsToNextTier, state.nextTier);
  const nearGoal = isNearTierGoal(state.spotsToNextTier, state.nextTier);
  const Icon =
    state.kind === 'verification' ? ShieldCheck :
    state.kind === 'add_photo' ? Camera :
    state.kind === 'write_review' ? MessageSquare :
    state.kind === 'publish_route' || state.kind === 'share_route' ? Route :
    state.kind === 'report_condition' ? Upload :
    CheckCircle2;
  const kicker =
    state.kind === 'verification' ? t('gamification.contribution.verification') :
    state.kind === 'add_photo' ? t('gamification.contribution.add_photo') :
    state.kind === 'write_review' ? t('gamification.contribution.write_review') :
    state.kind === 'report_condition' ? t('gamification.contribution.report_condition') :
    state.kind === 'publish_route' ? t('gamification.contribution.publish_route') :
    state.kind === 'share_route' ? t('gamification.contribution.share_route') :
    t('gamification.contribution.check_in');

  return (
    <div className="fixed left-1/2 -translate-x-1/2 bottom-24 z-[5000] w-[min(94vw,420px)] animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="relative overflow-hidden rounded-[1.75rem] border border-emerald-500/20 bg-[#101414]/95 shadow-[0_24px_70px_rgba(0,0,0,0.45)] backdrop-blur-xl">
        <div
          className="absolute inset-0 opacity-20 blur-2xl"
          style={{ background: `radial-gradient(circle at top left, ${state.currentTier.color}, transparent 60%)` }}
        />

        <div className="relative p-4">
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center border shrink-0"
              style={{
                color: state.currentTier.color,
                backgroundColor: `${state.currentTier.color}22`,
                borderColor: `${state.currentTier.color}44`,
              }}
            >
              <Icon size={20} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Sparkles size={12} className="text-emerald-400" />
                <p className="text-3xs font-black uppercase tracking-[0.2em] text-emerald-400">{kicker}</p>
              </div>

              <div className="mt-1 flex items-center justify-between gap-3">
                <h3 className="min-w-0 text-[14px] font-black text-[var(--text-primary)] truncate">{state.title}</h3>
                <span className="shrink-0 rounded-full border border-amber-400/20 bg-amber-400/10 px-2 py-1 text-2xs font-black text-amber-300">
                  +{state.rewardPoints} XP
                </span>
              </div>
              <p className="mt-1 text-tiny leading-relaxed text-[var(--text-secondary)]">{state.message}</p>

              <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Trophy size={13} style={{ color: state.currentTier.color }} />
                    <span className="text-2xs font-black uppercase tracking-widest text-[var(--text-primary)] truncate">
                      {t('gamification.contribution.level')} {state.currentTier.level} · {t(`tierGuide.tierNames.${state.currentTier.level}`, state.currentTier.name)}
                    </span>
                  </div>
                  <span className="text-2xs font-black text-[var(--text-muted)] whitespace-nowrap">
                    {state.nextTier
                      ? `${state.uniqueSpots}/${state.nextTier.threshold}`
                      : `${state.uniqueSpots}`}
                  </span>
                </div>

                <div className="mt-2 h-2 rounded-full bg-[var(--bg-input)] overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${nearGoal ? 'animate-pulse' : ''}`}
                    style={{
                      width: `${progressPct}%`,
                      backgroundColor: state.currentTier.color,
                      boxShadow: `0 0 ${nearGoal ? 18 : 12}px ${state.currentTier.color}${nearGoal ? 'aa' : '66'}`,
                    }}
                  />
                </div>

                {state.nextTier ? (
                  nearGoal ? (
                    <p className="mt-2 text-2xs font-black text-amber-300">
                      🔥 {t('gamification.contribution.almost_there', {
                        count: state.spotsToNextTier,
                        tier: t(`tierGuide.tierNames.${state.nextTier.level}`, state.nextTier.name),
                        defaultValue: 'Bare {{count}} igjen til {{tier}}!',
                      })}
                    </p>
                  ) : (
                    <p className="mt-2 text-2xs font-bold text-[var(--text-secondary)]">
                      {t('gamification.contribution.spots_remaining', { count: state.spotsToNextTier, tier: t(`tierGuide.tierNames.${state.nextTier.level}`, state.nextTier.name) })}
                    </p>
                  )
                ) : (
                  <p className="mt-2 text-2xs font-bold text-[var(--text-secondary)]">
                    {t('gamification.contribution.max_level')}
                  </p>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-white/5 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContributionProgressToast;
